import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, Clock, XCircle, Send } from "lucide-react";
import { Header } from "@/components/admin/ServicesTab";

const STATUS_MAP = {
  enviada: { icon: CheckCircle2, cls: "text-emerald-400", label: "Enviada" },
  pendente: { icon: Clock, cls: "text-amber-400", label: "Pendente" },
  enviando: { icon: Loader2, cls: "text-sky-400", label: "Enviando" },
  falha: { icon: XCircle, cls: "text-red-400", label: "Falha" },
};

export default function NotificationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/notifications");
      setItems(data);
    } catch { toast.error("Erro ao carregar notificações."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const retry = async (id) => {
    try {
      const { data } = await api.post(`/admin/notifications/${id}/retry`);
      toast.success(data.status === "enviada" ? "Notificação enviada." : `Status: ${data.status}`);
      load();
    } catch { toast.error("Erro ao reenviar."); }
  };

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-2">
        <Header title="Notificações WhatsApp" />
      </div>
      <div className="card-surface p-4 mb-5 text-sm text-stone-400 font-sans2">
        As notificações são geradas ao confirmar ou cancelar (pelo profissional) um agendamento. Enquanto o provedor
        de WhatsApp não estiver configurado nas variáveis de ambiente, elas ficam com status <span className="text-amber-400">Pendente</span> e podem ser reenviadas depois.
      </div>

      {loading ? (
        <div className="flex justify-center py-10 text-stone-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="card-surface p-8 text-center text-stone-400 font-sans2">Nenhuma notificação ainda.</div>
      ) : (
        <div className="notification-grid">
          {items.map((n) => {
            const sm = STATUS_MAP[n.status] || STATUS_MAP.pendente;
            const Icon = sm.icon;
            return (
              <div key={n.id} data-testid={`notification-item-${n.id}`} className="card-surface p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`flex items-center gap-1.5 text-xs font-sans2 ${sm.cls}`}>
                        <Icon className="w-4 h-4" /> {sm.label}
                      </span>
                      <span className="text-xs text-stone-500 font-sans2 capitalize">• {n.type}</span>
                      <span className="text-xs text-stone-500 font-mono2">• +{n.to}</span>
                    </div>
                    <p className="text-sm text-stone-300 font-sans2 mt-2 whitespace-pre-line">{n.message}</p>
                    <p className="text-[10px] text-stone-600 mt-2 font-mono2">
                      {new Date(n.created_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })} — {n.detail}
                    </p>
                  </div>
                  {!['enviada', 'enviando'].includes(n.status) && (
                    <button
                      data-testid={`retry-notification-${n.id}`}
                      onClick={() => retry(n.id)}
                      className="flex items-center gap-1.5 text-xs border border-[#282C37] text-stone-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] px-3 py-1.5 rounded-lg font-sans2 shrink-0 transition-colors"
                    >
                      <Send className="w-3.5 h-3.5" /> Reenviar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
